import React, { useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames/bind';
import { useAtom, useAtomValue } from 'jotai';

import { snackbarAtom } from '@/atoms/snackbar';

import EmptyLayout from '../EmptyLayout';
import CustomTravelLayout from '../CustomTravelLayout';
import GlobalHeader from './GlobalHeader';
import GlobalFooter from './GlobalFooter';

import styles from './GlobalLayout.module.scss';
import { MyPageHeader } from '@techminds/ui';
import { env } from '@/lib/env';
import { deviceAtom } from '@/atoms/device';
import BrandMallHeader from './BrandMallHeader';
import BrandMallFooter from './BrandMallFooter/BrandMallFooter';
import { MyMoFooter, MyPcFooter } from './MyFooter';

interface GlobalLayoutProps {
  myHeaderTitle?: string;
  children: React.ReactNode;
}

const cx = classNames.bind(styles);

const GlobalLayout: React.FC<GlobalLayoutProps> = ({ myHeaderTitle, children }) => {
  const device = useAtomValue(deviceAtom);
  const { pathname } = useRouter();
  const PageLayout = getPageLayout(pathname);
  const snackbarRef = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useAtom(snackbarAtom);

  const isMyPage = pathname.startsWith('/my');

  const isBrandMall = pathname.startsWith('/brandmall');

  useEffect(() => {
    if (!snackbarRef.current) {
      return;
    }

    const handleAnimationEnd = () => {
      snackbarRef.current?.removeEventListener('animationend', handleAnimationEnd);
      setMessage('');
    };

    snackbarRef.current.addEventListener('animationend', handleAnimationEnd);
  }, [message, setMessage]);

  if (pathname === '/login' || pathname === '/logout') {
    return <>{children}</>;
  }

  return (
    <div className={cx('GlobalLayout', isMyPage && 'as_mypage', isBrandMall && 'as_brandmall')}>
      <header className={cx('header')}>
        {isBrandMall ? (
          <BrandMallHeader />
        ) : !isMyPage ? (
          <GlobalHeader />
        ) : (
          <MyPageHeader
            title={myHeaderTitle || ''}
            onClose={() => window.location.assign(env('MY_PAGE_URL'))}
            travelInfoOutlink={env('TRAVEL_INFO_SERVICE_URL')}
            travelProductOutlink={env('VERTICAL_SERVICE_URL')}
          />
        )}
      </header>
      <main>
        <div>
          <PageLayout>{children}</PageLayout>
        </div>
      </main>
      <div className={cx('floating')}>
        <div id="snackbars" role="alert" aria-live="polite">
          {message && (
            <div data-snackbar ref={snackbarRef}>
              {message}
            </div>
          )}
        </div>
      </div>
      {isMyPage ? (
        device === 'mo' ? (
          <MyMoFooter />
        ) : (
          <MyPcFooter />
        )
      ) : isBrandMall ? (
        <BrandMallFooter />
      ) : (
        <footer className={cx('footer')}>
          <GlobalFooter />
        </footer>
      )}
    </div>
  );
};

const getPageLayout = (pathname: string) => {
  if (
    pathname.startsWith('/custom-travel/[agtName]/[productId]') ||
    pathname.startsWith('/custom-travel/estimate/[estimateId]')
  ) {
    return CustomTravelLayout;
  }

  return EmptyLayout;
};

export default GlobalLayout;
